import { FormEvent, useEffect, useRef, useState } from 'react'
import { api } from './api'
import { messageFor, Notice } from './ui'
import { SystemLayout } from './SystemAdmins'
import { sessionMethodLabel } from './AccountPages'

type SessionItem = { id: string; principal_id: string; name: string; email: string; company_id: string; company: string; auth_method: string; device_summary: string; purpose: string; delegated_by: string; auth_time: string; last_seen_at: string; expires_at: string; current: boolean }
type Page = { items: SessionItem[]; more: boolean }
type Company = { id: string; name: string }

const request = <T,>(path: string, init: RequestInit = {}) => api<T>(path, { ...init, signal: AbortSignal.timeout(20000) })
const when = (value: string) => new Date(value).toLocaleString()

export function sessionsQuery(company: string, search: string) {
  const params = new URLSearchParams()
  if (company) params.set('company', company)
  if (search) params.set('q', search)
  const query = params.toString()
  return query ? `?${query}` : ''
}

export function SystemSessions() {
  const [items, setItems] = useState<SessionItem[]>([])
  const [more, setMore] = useState(false)
  const [companies, setCompanies] = useState<Company[]>([])
  const [company, setCompany] = useState('')
  const [draft, setDraft] = useState('')
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [pending, setPending] = useState<SessionItem | null>(null)
  const [actionError, setActionError] = useState('')
  const [revoking, setRevoking] = useState(false)
  const latest = useRef(0)
  const query = sessionsQuery(company, search)

  const load = async () => {
    const id = ++latest.current
    setBusy(true)
    setError('')
    try {
      const page = await request<Page>(`/api/v1/system/sessions${query}`)
      if (id !== latest.current) return
      setItems(page.items)
      setMore(page.more)
    } catch {
      if (id === latest.current) setError('We could not load sessions. Try again.')
    } finally {
      if (id === latest.current) setBusy(false)
    }
  }
  useEffect(() => { void load() }, [query])
  useEffect(() => {
    request<{ tenants: Company[] }>('/api/v1/operator/overview').then(data => setCompanies(data.tenants ?? [])).catch(() => setCompanies([]))
  }, [])

  const revoke = async () => {
    if (!pending) return
    setRevoking(true)
    setActionError('')
    try {
      await request(`/api/v1/system/sessions/${encodeURIComponent(pending.id)}`, { method: 'DELETE' })
      setNotice(`Ended the session for ${pending.name || pending.email}. They need to sign in again on that device.`)
      setPending(null)
      await load()
    } catch (reason) {
      setActionError(messageFor(reason))
    } finally {
      setRevoking(false)
    }
  }

  return <SystemLayout active="sessions">
    <div className="page-heading">
      <div><h1>Sessions</h1><p>Everyone currently signed in, most recently active first. Ending a session signs that device out.</p></div>
      <button className="button secondary" disabled={busy} onClick={() => { void load() }}>Refresh</button>
    </div>
    {notice && <Notice tone="success">{notice}</Notice>}
    <section className="section-card">
      <form className="activity-filters" onSubmit={(event: FormEvent) => { event.preventDefault(); setSearch(draft.trim()) }}>
        <label>Company<select value={company} onChange={e => setCompany(e.target.value)}><option value="">All companies</option>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
        <label>Search<input type="search" value={draft} maxLength={100} placeholder="Name or email" onChange={e => setDraft(e.target.value)} /></label>
        <div className="activity-filter-actions"><button className="button secondary">Search</button></div>
      </form>
      {error && <><Notice tone="error">{error}</Notice><button className="button secondary" onClick={() => { void load() }}>Try again</button></>}
      {busy && <p role="status">Loading sessions…</p>}
      {!busy && !error && items.length === 0 && <p>No active sessions match.</p>}
      {items.length > 0 && <div className="table-scroll"><table>
        <thead><tr><th>Person</th><th>Company</th><th>Signed in with</th><th>Device</th><th>Last active</th><th></th></tr></thead>
        <tbody>{items.map(item => <tr key={item.id}>
          <td>{item.name || 'Unnamed'}{item.current && ' (this session)'}<br /><small>{item.email}</small>{item.delegated_by && <><br /><small>Opened by {item.delegated_by}</small></>}</td>
          <td>{item.company}</td>
          <td>{sessionMethodLabel(item.auth_method)}</td>
          <td>{item.device_summary}</td>
          <td>{when(item.last_seen_at)}<br /><small>Ends {when(item.expires_at)}</small></td>
          <td>{!item.current && <button className="button secondary" disabled={pending !== null} onClick={() => { setActionError(''); setPending(item) }}>End session</button>}</td>
        </tr>)}</tbody>
      </table></div>}
      {more && <p className="section-description">Showing the 200 most recently active sessions. Narrow by company or search to see others.</p>}
      {pending && <div className="admin-confirm" role="alertdialog" aria-label="Confirm ending session">
        <p>End the session for {pending.name || pending.email} at {pending.company} ({pending.device_summary})? They will be signed out on that device, along with any company access opened from it.</p>
        {actionError && <Notice tone="error">{actionError}</Notice>}
        <div className="admin-confirm-actions">
          <button className="button danger" disabled={revoking} onClick={() => { void revoke() }}>{revoking ? 'Ending…' : 'End session'}</button>
          <button className="button secondary" disabled={revoking} onClick={() => { setPending(null); setActionError('') }}>Cancel</button>
        </div>
      </div>}
    </section>
  </SystemLayout>
}
